import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Descriptions, Space, Spin, Table, Tag, Typography } from 'antd';
import { message } from '../../utils/antd-message';
import type { ColumnsType } from 'antd/es/table';
import {
  RECEIVABLE_TYPE_LABEL,
  billingApi,
  maskOrderMobile,
  type DunningRecipientView,
  type OrderDetail,
  type ReceivablePlan,
} from '../../api/billing.api';
import { DraggableModal } from '../../components/DraggableModal';
import { MoneyDisplay } from '../../components/MoneyDisplay';
import { calendarOverdueDays, fmtDateTime } from '../../utils/datetime';

const { Text } = Typography;

/** 未关注时无任何可发送收件人的前端兜底文案（与服务端 blockedHint 同口径）。 */
const NO_SUBSCRIBED_HINT =
  '家长当前未关注公众号，无法发送催缴提醒。建议引导家长关注公众号，或改用电话、短信等方式联系。';

/** 单个微信号的发送结果态（逐微信号独立记录，SC-06）。 */
type RecipientSendState =
  | { kind: 'idle' }
  | { kind: 'sending' }
  | { kind: 'sent'; skipped: boolean }
  | { kind: 'already' }
  | { kind: 'blocked'; hint: string };

/** 到期日的本地日期展示（YYYY-MM-DD）。 */
function fmtDate(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString('zh-CN') : '-';
}

export interface DunningModalProps {
  open: boolean;
  orderId: string;
  /** 订单摘要数据源（学生 / 监护人手机号 / 商品快照）。 */
  order: OrderDetail;
  /** 催缴目标节点（当前应收节点）。 */
  plan: ReceivablePlan;
  onClose: () => void;
}

/**
 * 催缴对话框（installment-dunning-admin-ui:SC-05~SC-08，ADM-10）。
 *
 * 摘要区 + 家长公众号关注列表 + 内容预览 + 逐微信号发送；无已关注微信号时
 * 整体阻断（无发送按钮），发送结果按后端 outcome 呈现（已提交 / 渠道开关
 * 关闭未发送 / 今日已发送 / 未关注阻断）。
 */
export function DunningModal({ open, orderId, order, plan, onClose }: DunningModalProps) {
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [guardianName, setGuardianName] = useState<string | null>(null);
  const [recipients, setRecipients] = useState<DunningRecipientView[]>([]);
  const [blockedHint, setBlockedHint] = useState<string | null>(null);
  const [sendStates, setSendStates] = useState<Record<string, RecipientSendState>>({});

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    setRecipients([]);
    setGuardianName(null);
    setBlockedHint(null);
    setSendStates({});
    billingApi
      .getDunningRecipients(orderId, plan.receivablePlanId)
      .then((result) => {
        if (cancelled) return;
        setGuardianName(result.guardianName);
        setRecipients(result.recipients);
        setBlockedHint(result.blockedHint);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, orderId, plan.receivablePlanId]);

  const overdueDays = plan.dueAt ? calendarOverdueDays(plan.dueAt) : 0;
  const unpaid = plan.amount - plan.paidAmount;
  const hasSubscribed = recipients.some((recipient) => recipient.subscribed);

  /** 催缴内容预览（格式对齐公众号模板与原型）。 */
  const previewText = useMemo(() => {
    const name = guardianName ?? '家长';
    return `【小象教育】尊敬的${name}家长，您为孩子${order.studentName}报名的「${order.productSnapshot.name}」${RECEIVABLE_TYPE_LABEL[plan.type]} ${(unpaid / 100).toFixed(2)} 将于 ${fmtDate(plan.dueAt)} 到期${overdueDays > 0 ? `（已逾期 ${overdueDays} 天）` : ''}，请尽快进入小程序完成支付。如有疑问请联系校区顾问。`;
  }, [guardianName, order.studentName, order.productSnapshot.name, plan.type, plan.dueAt, unpaid, overdueDays]);

  /** 逐微信号发送（SC-06：每次调用独立命令，结果独立标记）。 */
  const handleSend = async (recipient: DunningRecipientView) => {
    setSendStates((prev) => ({ ...prev, [recipient.wechatIdentityId]: { kind: 'sending' } }));
    try {
      const result = await billingApi.sendDunning(orderId, plan.receivablePlanId, recipient.wechatIdentityId);
      if (result.outcome === 'SENT') {
        setSendStates((prev) => ({
          ...prev,
          [recipient.wechatIdentityId]: { kind: 'sent', skipped: result.skippedBySwitch },
        }));
        if (result.skippedBySwitch) {
          message.warning('通知渠道已关闭，未发送；可前往系统设置开启公众号尾款通知');
        }
      } else if (result.outcome === 'ALREADY_SENT_TODAY') {
        setSendStates((prev) => ({
          ...prev,
          [recipient.wechatIdentityId]: { kind: 'already' },
        }));
      } else {
        setSendStates((prev) => ({
          ...prev,
          [recipient.wechatIdentityId]: {
            kind: 'blocked',
            hint: result.blockedHint ?? NO_SUBSCRIBED_HINT,
          },
        }));
      }
    } catch (err) {
      // 命令失败回置待发送，错误经全局提示呈现
      setSendStates((prev) => ({ ...prev, [recipient.wechatIdentityId]: { kind: 'idle' } }));
      message.error(err instanceof Error ? err.message : '催缴发送失败');
    }
  };

  /** 发送结果态单元格渲染（SC-06/SC-07）。 */
  const renderAction = (_: unknown, recipient: DunningRecipientView) => {
    const state = sendStates[recipient.wechatIdentityId] ?? { kind: 'idle' };
    if (state.kind === 'sending') return <Tag color="processing">发送中…</Tag>;
    if (state.kind === 'sent') {
      return state.skipped ? (
        <Tag color="orange">未发送：通知渠道已关闭</Tag>
      ) : (
        <Tag color="green">已发送</Tag>
      );
    }
    if (state.kind === 'already') return <Tag color="blue">今日已发送</Tag>;
    if (state.kind === 'blocked') return <Tag color="red">未关注，未发送</Tag>;
    // 未关注微信号不提供发送按钮（服务端必然阻断，SC-08 组件侧口径）
    if (!recipient.subscribed) return <Tag>未关注</Tag>;
    return (
      <Button size="small" type="primary" onClick={() => void handleSend(recipient)}>
        发送
      </Button>
    );
  };

  const recipientColumns: ColumnsType<DunningRecipientView> = [
    {
      title: '微信名称',
      dataIndex: 'nickname',
      render: (value: string | null) => value ?? '未知昵称',
    },
    {
      title: '关注时间',
      dataIndex: 'subscribeTime',
      render: (value: string | null) => (value ? fmtDateTime(value) : '-'),
    },
    { title: '操作', width: 160, render: renderAction },
  ];

  return (
    <DraggableModal open={open} title="发送催缴提醒" onCancel={onClose} footer={null} width={620} destroyOnHidden>
      <Descriptions size="small" column={2} bordered style={{ marginBottom: 16 }}>
        <Descriptions.Item label="学生">{order.studentName}</Descriptions.Item>
        <Descriptions.Item label="监护人手机号">{maskOrderMobile(order.guardianMobile)}</Descriptions.Item>
        <Descriptions.Item label="商品" span={2}>{order.productSnapshot.name}</Descriptions.Item>
        <Descriptions.Item label="收款节点">{RECEIVABLE_TYPE_LABEL[plan.type]}</Descriptions.Item>
        <Descriptions.Item label="待付金额"><MoneyDisplay amountInCents={unpaid} /></Descriptions.Item>
        <Descriptions.Item label="到期日">{fmtDateTime(plan.dueAt ?? '')}</Descriptions.Item>
        <Descriptions.Item label="逾期">
          {overdueDays > 0 ? <Text type="danger">{overdueDays} 天</Text> : '未逾期'}
        </Descriptions.Item>
      </Descriptions>

      <Text strong>公众号关注情况</Text>
      {loading ? (
        <div style={{ margin: '8px 0 16px' }}><Spin size="small" /></div>
      ) : failed ? (
        <Alert
          style={{ margin: '8px 0 16px' }}
          type="error"
          showIcon
          message="关注列表加载失败"
          description="请稍后重试，或改用电话等方式联系家长。"
        />
      ) : !hasSubscribed ? (
        <Alert
          style={{ margin: '8px 0 16px' }}
          type="warning"
          showIcon
          message="该学生家长尚未关注机构公众号，无法发送催缴提醒"
          description={blockedHint ?? NO_SUBSCRIBED_HINT}
          data-testid="dunning-blocked-alert"
        />
      ) : (
        <Table
          style={{ margin: '8px 0 16px' }}
          size="small"
          rowKey="wechatIdentityId"
          pagination={false}
          dataSource={recipients}
          columns={recipientColumns}
          data-testid="dunning-recipient-table"
        />
      )}

      <Text strong>催缴内容预览</Text>
      <div
        style={{
          margin: '8px 0',
          padding: 12,
          background: '#fafafa',
          border: '1px solid #f0f0f0',
          borderRadius: 8,
          fontSize: 13,
          lineHeight: 1.8,
        }}
        data-testid="dunning-preview"
      >
        {previewText}
      </div>
      <Text type="secondary" style={{ fontSize: 12 }}>
        确认内容无误后，在上方关注列表中选择接收的微信号逐条发送。
      </Text>

      <div style={{ textAlign: 'right', marginTop: 16 }}>
        <Space>
          <Button onClick={onClose}>关闭</Button>
        </Space>
      </div>
    </DraggableModal>
  );
}
